/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Ladda from 'ladda';
import moment from 'moment';

import SearchCustomerOrders from '@/services/SearchCustomerOrders';
import lazyLoadView from '@/util/lazyLoadView';
import { userHasRole } from '@/util/RoleUtils';
import GetInternalOrderStatuses from '@/services/GetInternalOrderStatuses';
import GetExternalOrderStatuses from '@/services/GetExternalOrderStatuses';

import loadSearchFields from '../../../login/loaders/SearchFieldsViewLoader';
import IntDashboardSidebarNavView from '../../../internal-dashboard/views/IntDashboardSidebarNavView';
import OrderDashboardGridView from '../../../order/views/OrderDashboardGridView/OrderDashboardGridView';
import GetAllOrderFormTypes from '../../../common/services/GetAllOrderFormTypes';
import GenerateReportCSV from '../../../administration/services/GenerateReportCSV';
import DownloadDocumentFromFS from '../../../common/util/downloadDocumentFromFS';

import tmplOrderDashboardPage from './OrderDashboardPage.hbs';

const OrderDashboardPage = View.extend({
  template: tmplOrderDashboardPage,

  ui: {
    sideBar: '.sidebar-nav-container',
    searchFieldsView: '.search-fields-view',
    orderDashboardGridView: '.order-dashboard-grid-view',
    btnDownloadReport: '.btn-download-report'
  },

  regions: {
    sideBar: '@ui.sideBar',
    searchFieldsView: '@ui.searchFieldsView',
    orderDashboardGridView: '@ui.orderDashboardGridView'
  },

  childViewEvents: {
    'search:response': 'onSearchResponse'
  },

  events: {
    'click @ui.btnDownloadReport': 'downloadReport'
  },

  onSearchResponse(response) {
    this.$('.result-count').empty().append(`<span><b>Total Result Count: ${response.totalCount}</b></span>`);
    this.orderDashboardGridView.collection.fullCollection.reset(response.orders);
  },

  onRender() {
    const isInternalUser = userHasRole('EPC') || userHasRole('CRS');
    const whichLookupService = isInternalUser ? GetInternalOrderStatuses : GetExternalOrderStatuses;
    const whichStatusName = isInternalUser ? 'statusID' : 'statusGroupID';

    if (isInternalUser) {
      this.showChildView('sideBar', new IntDashboardSidebarNavView({ mode: 'customer-orders' }));
    }

    const searchFields = [
      {
        id: 'order-id-search-field',
        name: 'customerOrderID',
        class: 'InputSearchField',
        placeholder: 'Order ID'
      },
      {
        id: 'order-type-search-field',
        name: 'typeID',
        class: 'SelectSearchField',
        placeholder: 'Type',
        lookupService: GetAllOrderFormTypes,
        lookupMapping: (type) => {
          const { formName, id } = type;
          return { optionText: formName, id };
        }
      },
      {
        id: 'order-status-search-field',
        name: whichStatusName,
        class: 'SelectSearchField',
        placeholder: 'Status',
        lookupService: whichLookupService,
        lookupMapping: (status) => {
          if (isInternalUser) {
            const { internalOrderStatus, id } = status;
            return { optionText: internalOrderStatus, id };
          } else {
            const { externalOrderStatus, id } = status;
            return { optionText: externalOrderStatus, id };
          }
        },
        lookupFilter: (status) => {
          if (isInternalUser) {
            return status.internalOrderStatus !== null;
          } else {
            return status;
          }
        }
      },
      {
        id: 'tracker-number-search-field',
        name: 'trackerNumber',
        class: 'InputSearchField',
        placeholder: 'Tracker Number'
      },
      {
        id: 'pop-start-date-start-search-field',
        name: 'popStartDateStart',
        class: 'DateSearchField',
        placeholder: 'POP Start Date Start'
      },
      {
        id: 'pop-start-date-end-search-field',
        name: 'popStartDateEnd',
        class: 'DateSearchField',
        placeholder: 'POP Start Date End'
      }
    ];

    this.showChildView(
      'searchFieldsView',
      lazyLoadView(() => loadSearchFields(searchFields, SearchCustomerOrders))
    );

    this.orderDashboardGridView = new OrderDashboardGridView({
      pageSize: 10,
      model: new Backbone.Model({})
    });
    this.showChildView('orderDashboardGridView', this.orderDashboardGridView);
  },

  async downloadReport(e) {
    e.preventDefault();
    const spinner = Ladda.create(this.ui.btnDownloadReport[0]);
    spinner.start();
    const reportFile = await GenerateReportCSV('18');
    const fileName = moment().format('YYYYMMDD').concat('_EMS_Orders_Report.csv');
    DownloadDocumentFromFS({
      tempFileName: reportFile,
      fileName
    });
    spinner.stop();
  }
});

export default OrderDashboardPage;
